import { Box, Flex, Text } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'

import type { DiagnosticsSnapshot } from './useDiagnosticsSnapshot'

// Strip the "Extension" prefix off the raw ExtensionType enum for display
// (e.g. "ExtensionLine" -> "Line", "ExtensionRingGroup" -> "Ring Group").
export const humanizeExtensionType = (raw: string): string =>
  raw
    .replace(/^Extension/, '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim() || raw

type Props = {
  extensions: DiagnosticsSnapshot['telephony']['extensions']
}

export const ExtensionsList = ({ extensions }: Props) => {
  const { t } = useTranslation()

  return (
    <>
      <Text
        fontSize="xs"
        fontWeight="semibold"
        color="fg.muted"
        textTransform="uppercase"
        letterSpacing="0.05em"
        mt={3}
        mb={1}
      >
        {t('statusPanel.extensions')} ({extensions.length})
      </Text>
      {extensions.length === 0 ? (
        <Text fontSize="sm" color="fg.muted">
          {t('statusPanel.noExtensions')}
        </Text>
      ) : (
        // Cap height so a user with many extensions scrolls within the
        // section instead of pushing the rest of the panel down.
        <Box maxH="15rem" overflowY="auto">
          {extensions.map((ext) => (
            <Flex key={ext.id} justify="space-between" gap={4} py={1.5}>
              <Flex align="center" gap={2} minW={0}>
                <Text
                  fontSize="sm"
                  color="fg.default"
                  fontFamily="mono"
                  flexShrink={0}
                >
                  {ext.number}
                </Text>
                <Text fontSize="sm" color="fg.muted" truncate>
                  {ext.name}
                </Text>
                {ext.isActiveSession && (
                  <Box
                    as="span"
                    fontSize="xs"
                    fontWeight="medium"
                    color="green.fg"
                    bg="green.subtle"
                    px={1.5}
                    borderRadius="sm"
                    flexShrink={0}
                  >
                    {t('statusPanel.thisSession')}
                  </Box>
                )}
              </Flex>
              <Text
                fontSize="sm"
                color="fg.muted"
                fontFamily="mono"
                textAlign="right"
                flexShrink={0}
              >
                {humanizeExtensionType(ext.type)}
              </Text>
            </Flex>
          ))}
        </Box>
      )}
    </>
  )
}
